import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { ColDef, GridOptions } from 'ag-grid-community';
import { dateComparator } from '../../shared/helpers/DateComparator';
import { CustomerNameCellComponent } from '../../shared/ag-grid-cells/customer-name-cell.component';
import { ReportCriteriaItemModel } from '../shared/models/report-criteria-item.model';
import { DateFormatPipe } from 'ngx-moment';
import { ConfigService } from '../../shared/services/config.service';
import { PaymentService } from '../../shared/services/payment.service';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PaymentDataSource } from '../../shared/helpers/PaymentDataSource';
import { BookingContractReferenceCellComponent } from '../../shared/ag-grid-cells/booking-contract-reference-cell.component';
import { UserNameCellComponent } from '../../shared/ag-grid-cells/user-name-cell.component';
import { DOCUMENT, NgStyle } from '@angular/common';
import { PaymentReportPaymentableCellComponent } from 'src/app/shared/ag-grid-cells/payment-report-paymentable-cell.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { MatButton } from '@angular/material/button';
import { ReportCriteriaComponent } from '../shared/report-criteria/report-criteria.component';
import { MatCard, MatCardContent } from '@angular/material/card';

@Component({
  selector: 'app-payments-report',
  templateUrl: './payments-report.component.html',
  styleUrls: ['./payments-report.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [DateFormatPipe, PaymentService],
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    ReactiveFormsModule,
    ReportCriteriaComponent,
    MatButton,
    AgGridModule,
    NgStyle,
    ExtendedModule
  ]
})
export class PaymentsReportComponent implements OnInit, OnDestroy {
  @ViewChild('formContainer', { static: true }) formContainer;
  @ViewChild('agGrid') grid!: AgGridAngular;

  columns: ColDef[] = [];

  availableCriteria: ReportCriteriaItemModel[] = [
    {
      name: 'Created Date',
      type: 'fromToDate',
      reference: 'created_at',
      used: true
    },
    { name: 'Amount', type: 'minMax', reference: 'amount', used: false },
    {
      name: 'Method',
      type: 'dropDown',
      reference: 'method',
      used: false,
      options: [
        { id: '*', value: 'All' },
        { id: 'cash', value: 'Cash' },
        { id: 'card', value: 'Card' },
        { id: 'cheque', value: 'Cheque' },
        { id: 'online', value: 'Online' },
        { id: 'Bank Transfer', value: 'Bank Transfer' }
      ],
      defaultValue: '*'
    },
    {
      name: 'Transferred',
      type: 'dropDown',
      reference: 'transferred',
      used: true,
      options: [
        { id: '*', value: 'All' },
        { id: 'Yes', value: 'Yes' },
        { id: 'No', value: 'No' }
      ],
      defaultValue: '*'
    },
    {
      name: 'Deleted',
      type: 'dropDown',
      reference: 'deleted',
      used: true,
      options: [
        { id: '*', value: 'All' },
        { id: 'Yes', value: 'Yes' },
        { id: 'No', value: 'No' }
      ]
    }
  ];

  gridOptions: GridOptions = {
    rowModelType: 'infinite',
    paginationPageSize: 31,
    cacheBlockSize: 31,
    headerHeight: 45,
    cacheOverflowSize: 2,
    maxConcurrentDatasourceRequests: 2,
    infiniteInitialRowCount: 1,
    rowMultiSelectWithClick: true,
    getRowStyle: (params) => {
      if (params.node.rowPinned) {
        return { fontWeight: 'bold' };
      }
    }
  };
  height = 400;

  orderBy = {
    order: 'id',
    direction: 'asc'
  };

  reportForm = new UntypedFormGroup({});
  private ngUnsubscribe = new Subject<void>();
  reportFetched = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private paymentService: PaymentService,
    private datePipe: DateFormatPipe,
    private configService: ConfigService
  ) {
    this.configService.allUsers
      .pipe(
        filter((users) => !!users),
        map((users) => {
          return users.map((user) => {
            return { id: user.id, value: user.name };
          });
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((userOptions: any[]) => {
        userOptions.push({ id: '*', value: 'All Users' });
        const newCriterieaItem: ReportCriteriaItemModel = {
          name: 'User',
          type: 'dropDown',
          reference: 'user_id',
          used: false,
          defaultValue: '*',
          options: userOptions
        };
        this.addOrOverwriteCriteriaItem(newCriterieaItem);
      });
    this.configService.locations
      .pipe(
        filter((locations) => !!locations),
        map((locations) => {
          return locations.map((location) => {
            return { id: location.id, value: location.name };
          });
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((locationOptions: any[]) => {
        locationOptions.push({ id: '*', value: 'All locations' });
        const newCriteriaItem: ReportCriteriaItemModel = {
          name: 'Location',
          type: 'dropDown',
          reference: 'location_id',
          defaultValue: '*',
          used: false,
          options: locationOptions
        };
        this.addOrOverwriteCriteriaItem(newCriteriaItem);
      });
  }

  ngOnInit() {
    this.columns = [
      {
        headerName: 'Customer',
        cellRendererSelector: (params) => {
          if (params.node.rowPinned) {
            return {
              component: undefined,
              params: {
                style: { color: '#5577cc' }
              }
            };
          }
          return {
            component: CustomerNameCellComponent
          };
        },
        field: 'full_name'
      },
      {
        headerName: 'Docket',
        cellRendererSelector: (params) => {
          if (params.node.rowPinned) {
            console.log('row pinned!', { params });
            return {
              component: (...p) => {
                return 'Total: ';
              }
            };
          }
          return {
            component: PaymentReportPaymentableCellComponent
          };
        },
        valueGetter: (params) => {
          if (!params.data) {
            return null;
          }
          return {
            type: params.data.paymentable_type,
            id: params.data.paymentable_id,
            invoice_number: params.data.accounts_reference
          };
        }
      },
      {
        headerName: 'Amount',
        field: 'amount',
        valueFormatter: (params) => {
          if (params.data?.amount) {
            return `${(params.data['amount'] / 100).toFixed(2)}`;
          }
        }
      },
      {
        headerName: 'User',
        field: 'user_id',
        cellRenderer: UserNameCellComponent
      },
      {
        headerName: 'Location',
        field: 'location'
      },
      { headerName: 'Method', field: 'method' },
      {
        headerName: 'Date',
        field: 'created_at',
        valueGetter: (params) => {
          if (params.data) {
            return this.datePipe.transform(
              params.data['created_at'],
              ConfigService.dates.shortDateTimeFormat
            );
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Transferred',
        field: 'transferredDate',
        valueGetter: (params) => {
          if (params.data) {
            return this.datePipe.transform(
              params.data['transferredDate'],
              ConfigService.dates.shortDateTimeFormat
            );
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Deleted',
        field: 'deleted'
      }
    ];
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getPayments(isExport = false) {
    this.reportFetched = true;
    let used = {};
    this.availableCriteria.forEach((available) => {
      if (available.used) {
        used[available.reference] = {
          type: available.type,
          value: available.formControl.value
        };
      }
    });
    if (isExport) {
      this.exportReporToXls(used);
      return;
    }
    this.calculateTableHeight();
    const dataSource = new PaymentDataSource(
      this.paymentService,
      used,
      this.orderBy
    );
    this.grid.api.setDatasource(dataSource);

    this.paymentService
      .generateReport({ summary: true }, used)
      .subscribe((results) => {
        this.grid.api.setPinnedBottomRowData([{ ...results }]);
      });
    this.reportForm.markAsPristine();
  }

  exportReporToXls(used) {
    this.paymentService
      .generateReportXls(
        {
          excludeDeleted: 1,
          export: true
        },
        used
      )
      .subscribe((data) => {
        const blob = new Blob([data] as any, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = this.document.defaultView.URL.createObjectURL(blob);
        const a = this.document.createElement('a');
        this.document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = url;
        a.download = 'Payments Report.xlsx';
        a.click();
        this.document.defaultView.URL.revokeObjectURL(url);
        a.remove(); // remove the element
      });
  }

  @HostListener('window:resize', ['$event'])
  calculateTableHeight() {
    const height =
      this.document.defaultView.innerHeight -
      this.formContainer.nativeElement.getBoundingClientRect().bottom -
      150;
    this.height = height < 300 ? 300 : height;
    if (this.grid.api) {
      this.grid.api.autoSizeAllColumns();
    }
  }

  private addOrOverwriteCriteriaItem(newCriteriaItem: ReportCriteriaItemModel) {
    let existingIndex = this.availableCriteria
      .map((item) => item.reference)
      .indexOf(newCriteriaItem.reference);
    if (existingIndex > -1) {
      this.availableCriteria.splice(existingIndex, 1, newCriteriaItem);
      return;
    }
    this.availableCriteria.push(newCriteriaItem);
  }
}
